/* ==========================================================================
   SPLASH Duel — the data module, wired to the chain layer.

   Third and last of the three ports. It replaces the candidate build's
   hand-written rehearsal file and publishes the same `window.SplashDuel`
   object, so duel-view.js is the designer's file untouched.

   THE SAME RULE AS ITS TWO SIBLINGS: read the constants, never restate them.
   The stake ladder comes from the realm's own StakeOfRung.

   REDESIGNED 2026-10-08, AND THIS IS THE DATA-LAYER SLICE OF THAT REDESIGN.
   The duel was redeployed with a completely different contract: there is no
   secret, no nonce, no commitment, no reveal, no deadline, and no forfeit.
   The whole write surface is three calls --
   Post(rung, opponent, label), Accept(offerID), Resolve(duelID) -- and the
   last of those is PERMISSIONLESS and pays AND deletes the record in one
   call, decided by the beacon's randomness at the round Accept pinned.

   WHAT THAT REMOVES FROM THIS FILE. putUp() and take() used to be the
   dangerous half of this module: a secret only the poster held, saved to
   chain/vault.js BEFORE the transaction that needed it, because losing it
   forfeited the whole duel. None of that exists any more -- chain/coin.js
   and chain/vault.js are deleted (see this change's own notes for what was
   checked before deleting them), and putUp/take are now exactly as simple
   as the realm's own ABI: build the call, batch it with wrap+approve, sign
   once, refresh.

   THIS SLICE (2026-10-08, VIEW SLICE): DuelOf's fourth value is a PINNED
   BEACON ROUND, not a reveal deadline -- "the most important number on this
   surface" because it is the fairness claim made checkable: a player can
   open drand's own site and watch that exact round not exist, then exist.
   refreshBeacon() (below) turns it into a wall-clock countdown via the
   beacon's TimeOfRound(round)/CurrentRound(), and compose()'s `air` hands
   decidedAt/due/published through honestly -- all three null together when
   there is no real beacon to ask (rehearsal) or no answer has landed yet,
   never guessed or defaulted.
   ========================================================================== */
import './polyfill.js';
import { CONFIG } from './config.js';
import * as live from './chain/reads.js';
import { readBoard } from './chain/board.js';
import * as wallet from './chain/wallet.js';
import { realmAddress } from './chain/addr.js';
import { createDuelCompat } from './chain/compat.js';

const REALM = CONFIG.duelRealm;

// How many messages the panel shows. The realm keeps 64; showing all of them
// in a side panel is a wall, and the room's purpose is the last minute or two
// around a settlement, not an archive. Everything older is still on chain.
const CHAT_SHOW = 24;

// Every place below that actually signs something calls through here rather
// than `wallet.keySend`/`wallet.adenaSend` directly. Production never
// touches this: it is populated with the real functions and nothing
// reassigns it. It exists so test/duelcompat.test.mjs can substitute a spy
// and assert, directly, that an incompatible realm never reaches a signer --
// not just that the right message came back, which a reordering bug could
// satisfy by accident.
export const ports = { keySend: wallet.keySend, adenaSend: wallet.adenaSend };

// ugnot -> GNOT, and ONLY at the boundary, as in weekly-data.js.
const gnot = (u) => Number(u) / 1e6;

const S = {
  join: null,        // the next accept's flip + when that window closes; refreshJoin()
  bucketRounds: null,
  posterOf: new Map(),  // duel id -> poster, captured while in flight; see compose()
  seenSettled: null,    // ids already announced, so a flip fires once and only once
  chat: null,
  chatMaxLen: 280,   // replaced by the realm's own MaxTextLen() at boot
  mode: 'rehearsal',
  client: null,
  stakes: null,
  resultRingSize: null,
  chain: null,
  liveStatus: null,
  deployed: false,
  board: null,
  // RecentResults' answer, most-recent-first, re-fetched alongside the board
  // every refresh(). Empty array (not null) once a read has actually
  // succeeded at least once, so compose() can tell "no results yet" apart
  // from "never asked" without a third flag.
  recent: null,
  me: null,
  master: null,
  err: {},
  // compat: the per-load guard instance (chain/compat.js), or null in
  // rehearsal, where there is no real contract to be the wrong version of.
  // compatResult: that guard's settled answer, re-published in every state
  // so the view's banner survives refreshes rather than flashing once.
  compat: null,
  compatResult: null,
  // beacon: this poll's answer to "has the round pinned on each in-flight
  // duel arrived, and has anyone published it" -- null until the first live
  // refresh, and null forever in rehearsal (there is no real beacon to ask).
  // See refreshBeacon() below. currentRound/times/published are read fresh
  // every poll rather than computed from the browser's own clock, on
  // purpose: CurrentRound() is the CHAIN's "now", not this tab's.
  beacon: null,
};

const listeners = [];
let state = null;

function emit(evt) {
  for (const fn of listeners) {
    try { fn(evt); } catch (e) { console.error('SplashDuel listener threw', e); }
  }
}
const publish = () => emit({ type: 'state', state });
const fail = (message) => emit({ type: 'error', message });

/* --- the live client, assembled from the read functions ------------------- */
// readBoard asks for an object with these six methods. reads.js exports them as
// free functions, so this is the adapter rather than a second implementation --
// the rehearsal in chain/mock.js already answers the same interface, which is
// what lets one readBoard serve both. recentResults/resultRingSize are called
// directly against S.client in refresh() below, not through readBoard (they are
// not part of the board at all -- RecentResults is the only trace a SETTLED
// duel leaves, and the board only ever holds duels still in flight).
const liveClient = {
  offerCount: live.offerCount,
  duelCount: live.duelCount,
  offerIds: live.offerIds,
  duelIds: live.duelIds,
  offerOf: live.offerOf,
  duelOf: live.duelOf,
  escrowed: live.escrowed,
  recentResults: live.recentResults,
  resultRingSize: live.resultRingSize,
};

/* --- the shape the view asks for ------------------------------------------ */
function compose() {
  const b = S.board;
  if (!b || !S.stakes) {
    state = { offline: true, error: (S.err.board ?? S.err.chain)?.message ?? 'the realm could not be read' };
    return state;
  }

  const height = S.chain ? BigInt(S.chain.height) : null;

  // An offer standing in the lobby. `target` is null for an open offer and an
  // address for a challenge aimed at one player -- the realm returns "" for
  // open, and "" is not null, so the view would print an empty challenge chip
  // if that were passed straight through. UNCHANGED BY THE REDESIGN: OfferOf
  // never carried a commitment to begin with in this shape (reads.js's
  // offerOf already split money from the challenge fields the same way), so
  // there was nothing here to remove.
  const table = b.offers.map((o) => ({
    id: Number(o.id),
    by: o.poster,
    stake: gnot(o.stake),
    message: o.label || '',
    target: o.opponent || null,
    postedAt: Number(o.postedAt),
  }));

  // A duel in flight. EVERY RECORD HERE IS UNRESOLVED BY CONSTRUCTION:
  // Resolve pays AND DELETES in the same call, so a duel that answers DuelOf
  // at all has not settled -- there is no `.resolved` to filter on any more,
  // unlike the old shape this replaced.
  //
  // `round` IS THE PINNED BEACON ROUND -- "the most important number on this
  // surface" -- handed through AS A ROUND, never disguised as a block height
  // the way the old `revealBy` read. `decidedAt`/`due`/`published` are THIS
  // SLICE's countdown, built from S.beacon (refreshBeacon(), below) rather
  // than computed here from scratch:
  //   decidedAt -- the unix second this round is signed (TimeOfRound), for
  //                 the ticking "in 4:12" the view draws every second.
  //   due       -- whether the CHAIN's own clock (CurrentRound(), not this
  //                 tab's Date.now()) says that second has already arrived.
  //   published -- whether anyone has put that round's randomness on chain
  //                 yet. Only meaningful once `due` is true; null otherwise.
  // All three are `null` together exactly when S.beacon has no answer yet
  // (rehearsal, forever; live, before the first beacon read lands) -- never
  // guessed, never defaulted to "not due" or "not published".
  //
  // WHAT IS GONE AND NOT REPLACED: `status` ('waiting'/'settle'/'unsettled'),
  // `openBy` (a block height), and `takerReveal` (whether this browser still
  // owed a RevealNonce). None of those concepts exist on the new contract --
  // there is no deadline, no forfeit, and no second commitment to reveal --
  // so this record does not fake them.
  const air = b.duels.map((d) => {
    const key = d.round == null ? null : String(d.round);
    const decidedAt = key != null ? S.beacon?.times.get(key) ?? null : null;
    const curRound = S.beacon ? S.beacon.currentRound : null;
    const due = (key != null && curRound != null) ? BigInt(d.round) <= BigInt(curRound) : null;
    const published = key != null ? S.beacon?.published.get(key) ?? null : null;
    return {
      id: Number(d.id),
      creator: d.poster,
      taker: d.taker,
      stake: gnot(d.stake),
      message: d.label || '',
      round: d.round == null ? null : Number(d.round),
      acceptedAt: d.acceptedAt == null ? null : Number(d.acceptedAt),
      decidedAt: decidedAt == null ? null : Number(decidedAt),
      due,
      published,
    };
  });

  // A settled duel, FROM RecentResults -- THE ONLY PLACE ONE EXISTS ANY MORE.
  // Resolve deletes the Offer/Duel record the instant it pays, so there is no
  // board read for a settled duel at all: RecentResults (the realm's own
  // last-32-most-recent-first ring, ResultRingSize() read live rather than
  // hardcoded) is it. Anything older is in the event log only, which this
  // page does not read.
  //
  // RecentResults DOES NOT SAY WHO WAS POSTER AND WHO WAS TAKER -- it reports
  // Winner and Loser, nothing else. `creator`/`taker` below are therefore
  // WINNER/LOSER, which is what duel-view.js's "did I win" check actually
  // needs (it only ever asks "is this address me", never "was I the
  // poster"). `round` IS REAL -- the pinned beacon round that decided this
  // duel, the same fairness claim the countdown makes checkable, now closing
  // the loop: a settled row names the exact round a player could have
  // watched not exist, then exist. (The old `face` placeholder -- 'droplet'
  // always, never a real read of which side landed -- is gone from this
  // record along with the "droplet up"/"waves up" flavour text it drove;
  // `round` is what replaces it, because it is the one true thing this
  // record can say about how the flip was decided.)
  // THE FLIGHT: EVERY DUEL ON ONE ROUND, AS ONE EVENT.
  //
  // This is the mechanic the contract was built around and the page was
  // throwing away. duel.gno's bucketRounds comment says it plainly: "EVERY
  // DUEL ACCEPTED IN THE SAME BUCKET SHARES A ROUND, which is the communal
  // settling moment the owner asked for". Pins are rounded UP to a shared
  // boundary precisely so that a dozen duels land on one number at one
  // second -- and the UI was rendering them as a dozen unrelated rows each
  // with its own private countdown.
  //
  // So: group the air by round. No contract constants are needed for this --
  // it is just the real rounds the realm already pinned, counted. Sorted
  // earliest first, so flights[0] is the next thing that happens.
  const flights = (() => {
    const by = new Map();
    for (const d of air) {
      if (d.round == null) continue;
      const k = String(d.round);
      const f = by.get(k) ?? {
        round: d.round, count: 0, staked: 0,
        decidedAt: d.decidedAt, due: d.due, published: d.published,
        mine: false,
      };
      f.count += 1;
      f.staked += d.stake * 2;          // both sides are in the pot
      if (S.me != null && (d.creator === S.me || d.taker === S.me)) f.mine = true;
      by.set(k, f);
    }
    return [...by.values()].sort((a, b) => a.round - b.round);
  })();

  // REMEMBER WHO POSTED, WHILE THE DUEL IS STILL IN THE AIR. RecentResults
  // gives winner and loser, not poster and taker, so once a duel settles there
  // is no way to tell which side the winner was on -- and the coin's faces are
  // poster-vs-taker by convention (see ui/coinanim.js). The air list has it, so
  // it is captured here, every poll, for the only duels it could ever matter
  // for: the ones in flight right now.
  for (const d of air) S.posterOf.set(d.id, d.creator);

  const settled = (S.recent ?? []).map((r) => ({
    id: Number(r.duelId),
    creator: r.winner,
    taker: r.loser,
    stake: gnot(S.stakes[r.rung]),
    round: r.round == null ? null : Number(r.round),
    block: r.height == null ? null : Number(r.height),
  }));

  state = {
    rehearsal: S.mode === 'rehearsal',
    network: CONFIG.chainId,
    // The REAL chain's height, even in rehearsal -- what makes a rehearsal
    // visibly a rehearsal rather than a mock pretending to be a chain.
    networkHeight: S.liveStatus ? Number(S.liveStatus.height) : null,
    currentBlock: height == null ? null : Number(height),
    // rakeBps is the one constant CONFIG carries rather than the realm, with a
    // comment there saying it must track the contracts. toDraw is the players'
    // share of that rake, which raffle_test.gno pins at 60%.
    rake: { share: Number(CONFIG.rakeBps) / 10000, toDraw: 0.6 },
    stakes: S.stakes.map(gnot),
    me: S.me,
    table,
    air,
    settled,
    // RecentResults' own ring size (read live, ResultRingSize()), so the
    // view can say truthfully how much history "Settled lately" covers --
    // anything older than this lives only in the chain's event log.
    resultRingSize: S.resultRingSize ?? null,
    wallet: S.me ? { address: S.me } : null,
    realm: REALM,
    // null in rehearsal (nothing to check); otherwise the compat guard's
    // settled { ok, reason, message } -- see chain/compat.js and putUp/take's
    // own assertCompatible, which is what actually refuses the write. This
    // copy is for the banner only.
    compat: S.compatResult,
    // THE ROOM. Null in rehearsal (the mock has no chat realm) and null if
    // the read failed -- the view hides the panel rather than showing an
    // empty room, because "nobody has said anything" and "we could not ask"
    // are different and only one of them is the room's fault.
    chat: S.chat,
    // Every pending round as one event, earliest first. flights[0] is the
    // next flip; the rest are the ones forming behind it.
    flights,
    // Which flip a NEW acceptance joins, and when that window shuts. Null
    // whenever the realm's own answer did not confirm the derived boundary.
    join: S.join,
  };
  return state;
}

/* --- the poll ------------------------------------------------------------- */
async function refresh() {
  const jobs = await Promise.allSettled([
    S.mode === 'rehearsal' && S.client.chainStatus ? S.client.chainStatus() : live.chainStatus(),
    readBoard(S.client, S.stakes),
    S.client.recentResults(S.resultRingSize ?? 32),
  ]);
  const [st, bd, rr] = jobs;

  S.err = {};
  if (st.status === 'fulfilled') S.chain = st.value; else { S.chain = null; S.err.chain = st.reason; }
  if (bd.status === 'fulfilled') S.board = bd.value; else { S.board = null; S.err.board = bd.reason; }
  // A failed RecentResults read is NOT fatal to the page the way a failed
  // board read is: it keeps the last answer on screen rather than blanking
  // the settled list over one flaky poll. `?? []` only fires on the very
  // first read, before anything has ever succeeded.
  if (rr.status === 'fulfilled') S.recent = rr.value; else { S.recent = S.recent ?? []; S.err.recent = rr.reason; }

  await refreshBeacon();
  await refreshChat();
  await refreshJoin();

  compose();
  if (state.offline) { fail(state.error); return; }
  publish();
  announceSettlements();
}

// THE COIN NEVER SPUN, AND THE WHOLE ANIMATION WAS ALREADY BUILT.
//
// duel-view.js has had onFlip() the entire time -- the tumble, the landing,
// the ripple, the reduced-motion path, the result panel. public/ui/css/duel.css
// has the keyframes. It listens for an event of type 'flip'. NOTHING IN THIS
// FILE HAS EVER EMITTED ONE. So a duel settled, the boards updated, and the
// coin sat perfectly still.
//
// THAT IS THE FOURTH INSTANCE TODAY of one thing being correct and complete
// while nobody calls it -- after beacon.Publish, duel.Resolve and
// duel.FlushPot. The other three were on the chain side; this one is the
// front end doing it to itself. Worth naming as a pattern rather than a
// coincidence: both halves of a seam get built, each is tested on its own,
// and the seam is what nobody owns.
//
// FIRES ONCE PER DUEL, EVER. seenSettled starts null and is filled on the
// first poll WITHOUT announcing anything -- otherwise every page load would
// replay the last 32 settlements as a storm of coin flips. Only duels that
// appear after that first snapshot are new.
function announceSettlements() {
  const ids = new Set(state.settled.map((d) => d.id));
  if (S.seenSettled === null) { S.seenSettled = ids; return; }

  for (const d of state.settled) {
    if (S.seenSettled.has(d.id)) continue;

    // RecentResults names the winner and loser, not the poster and taker.
    // posterOf remembers it from while the duel was in the air; if this page
    // was not open then, we genuinely do not know, and the face falls back to
    // the player's own result rather than guessing a side.
    const poster = S.posterOf.get(d.id) ?? null;
    const youWon = S.me != null && d.creator === S.me;   // creator is the WINNER here
    const youIn = S.me != null && (d.creator === S.me || d.taker === S.me);
    const posterWins = poster == null ? null : d.creator === poster;

    const pool = d.stake * 2;
    const rake = pool * (Number(CONFIG.rakeBps) / 10000);
    emit({
      type: 'flip',
      result: {
        id: d.id,
        // droplet = the poster took it, waves = the taker did. When the poster
        // is unknown, fall back to the viewer's own outcome -- still honest,
        // just a different convention, and only reachable for a duel that
        // settled before this page was opened.
        face: (posterWins ?? youWon) ? 'droplet' : 'waves',
        youIn,
        youWon,
        youHad: poster != null && S.me === poster ? 'droplet' : 'waves',
        opponent: youWon ? d.taker : d.creator,
        stake: d.stake,
        payout: pool - rake,
        rakeToDraw: rake * 0.6,
        rakeToCosts: rake * 0.4,
        block: d.block,
      },
    });
  }
  S.seenSettled = ids;
}

// refreshBeacon: the countdown's own data, read fresh every poll. Live mode
// only -- there is no real beacon to ask in rehearsal, so S.beacon stays
// null and compose() hands decidedAt/due/published through as null rather
// than inventing a countdown this file has no chain to back up.
//
// CurrentRound() is fetched once per poll and shared across every duel;
// TimeOfRound(round) and Published(round) are fetched once PER DISTINCT
// ROUND (duels matched in the same five-minute window share a round, so
// this is almost always fewer reads than one per duel). Published(round) is
// asked for every round rather than only the ones already due, because
// "due" itself depends on CurrentRound() landing first -- asking for both
// together in one Promise.all is simpler than a second round-trip, and
// harmless: an unpublished future round just answers false.
//
// A failed read here is not fatal to the page the way a failed board read
// is: it keeps the previous poll's answer (or null, before the first one
// ever lands) rather than blanking every countdown over one flaky call.
// THE ROOM, POLLED ALONGSIDE THE BOARD. Separate from refresh() for the same
// reason refreshBeacon is: a flaky chat read must never blank the duel board,
// which is the part with money on it. A failure here leaves the last messages
// on screen and sets nothing.
//
// WHY THE SETTLEMENT MOMENT NEEDS THIS AT ALL: duels bucket onto SHARED drand
// rounds by design, so a dozen of them land on the same number at the same
// second. That is the moment worth being in a room for, and without one it is
// a dozen people each alone with a spinner.
async function refreshChat() {
  if (S.mode !== 'live') { S.chat = null; return; }
  try {
    const [msgs, seen] = await Promise.all([live.chatRecent(CHAT_SHOW), live.chatSeen()]);
    S.chat = {
      messages: msgs.map((m) => ({
        id: Number(m.id),
        author: String(m.author),
        text: String(m.text),
        height: Number(m.height),
        mine: S.me != null && String(m.author) === S.me,
      })),
      seen: Number(seen),
      maxLen: S.chatMaxLen,
    };
  } catch (e) {
    // Leave the last good answer up. Only null on the very first failure,
    // which is when there is nothing to keep anyway.
    if (!S.chat) S.chat = null;
  }
}

// THE JOIN DEADLINE: "take one now and you are in the 21:47 flip".
//
// The owner asked for this ("four minutes left to flip, get your challenges
// in") and I could not build it honestly until duel/v4 exported the pin
// arithmetic on 2026-10-08. The numbers are a SECURITY BUDGET; a JavaScript
// copy of one drifts the moment anybody retunes it, so the realm is asked.
//
// COMPUTE A GUESS, THEN CHECK IT AGAINST THE REALM. The boundary where the
// answer changes is derivable from bucketRounds alone -- the next multiple of
// the bucket above the current round -- but deriving is exactly how a second
// copy of contract logic gets born. So: derive the candidate, then ASK the
// realm what it would pin on either side of it, and only believe the deadline
// if the realm's own answer actually changes there. If it does not, show
// nothing rather than a number I cannot stand over.
async function refreshJoin() {
  if (S.mode !== 'live' || !S.beacon || S.beacon.currentRound == null) { S.join = null; return; }
  try {
    const cur = Number(S.beacon.currentRound);
    if (!Number.isFinite(cur) || cur <= 0) { S.join = null; return; }
    const bucket = S.bucketRounds ?? Number(await live.duelBucketRounds());
    S.bucketRounds = bucket;
    if (!bucket) { S.join = null; return; }

    const boundary = (Math.floor(cur / bucket) + 1) * bucket;
    const [now, after] = await Promise.all([
      live.duelRoundForAcceptAt(cur),
      live.duelRoundForAcceptAt(boundary),
    ]);
    if (Number(now) === Number(after)) { S.join = null; return; }  // guess was wrong; say nothing

    // The deadline in wall-clock terms is when the chain's beacon reaches
    // `boundary`. TimeOfRound is the beacon's own clock, not ours.
    const closesAt = Number(await live.timeOfRound(boundary));
    const flipAt = Number(await live.timeOfRound(Number(now)));
    S.join = { round: Number(now), closesAt, flipAt };
  } catch (e) {
    S.join = null;
  }
}

async function refreshBeacon() {
  if (S.mode !== 'live' || !S.board) { S.beacon = null; return; }
  const rounds = [...new Set(S.board.duels.map((d) => d.round).filter((r) => r != null).map(String))];
  // STILL READ currentRound WITH AN EMPTY BOARD. This used to bail out here
  // with currentRound: null, which was fine when the only consumer was a
  // per-duel countdown -- no duels, nothing to count. The join line broke that
  // assumption: "take a stake in the next 2:14 and you are in the 21:47 flip"
  // is MOST useful when nothing is in the air, because that is exactly when
  // somebody needs inviting in. One read.
  if (rounds.length === 0) {
    try {
      S.beacon = { currentRound: await live.currentRound(), times: new Map(), published: new Map() };
    } catch (e) {
      S.beacon = { currentRound: null, times: new Map(), published: new Map() };
    }
    return;
  }
  try {
    const [cur, times, published] = await Promise.all([
      live.currentRound(),
      Promise.all(rounds.map((r) => live.timeOfRound(r))),
      Promise.all(rounds.map((r) => live.beaconPublished(r))),
    ]);
    S.beacon = {
      currentRound: cur,
      times: new Map(rounds.map((r, i) => [r, times[i]])),
      published: new Map(rounds.map((r, i) => [r, published[i]])),
    };
  } catch (e) {
    S.err.beacon = e;
    // keep whatever S.beacon already held (or null, on a first failure)
  }
}

async function start() {
  const q = new URLSearchParams(location.search);
  const forceDemo = q.has('demo') || location.hash === '#rehearsal';

  try {
    S.liveStatus = await live.chainStatus();
    S.deployed = forceDemo ? false : await live.isDeployed(REALM);
  } catch (e) {
    console.warn('chain unreachable', e);
    S.err.chain = e;
  }

  if (S.deployed) {
    S.mode = 'live';
    S.client = liveClient;
    S.stakes = await live.ladder();
    S.resultRingSize = await live.resultRingSize();
    // ONE QUERY FOR THE WHOLE PAGE LOAD. createDuelCompat memoizes its own
    // check(), so this await settles it once, here, and putUp/take's later
    // calls (one per attempted Post or Accept) reuse the same settled
    // answer instead of asking the chain again. Awaited before the first
    // refresh() so the very first published state already carries the
    // right banner rather than one tick behind.
    S.compat = createDuelCompat(REALM, live.hasFunction);
    S.compatResult = await S.compat.check();
    // The ROOM'S OWN cap, so the character counter and the disabled state
    // match what the contract will actually accept. Hardcoding 280 here
    // would be a second copy of a number the realm already publishes, and
    // the two would drift. Failure is survivable -- the fallback is the
    // current value and the contract refuses anything longer anyway.
    try { S.chatMaxLen = Number(await live.chatMaxTextLen()); } catch { /* keep the default */ }
  } else {
    // The duel's rehearsal lives in chain/mock.js and already answers readBoard's
    // interface -- it is the same mock the old duel page used.
    const { makeMock } = await import('./chain/mock.js');
    S.mode = 'rehearsal';
    S.client = makeMock();
    S.stakes = await S.client.ladder?.() ?? [10_000_000n, 50_000_000n, 200_000_000n, 1_000_000_000n];
    S.resultRingSize = await S.client.resultRingSize?.() ?? 32;
    S.me = S.client.me ?? null;
  }

  // Carry a wallet across pages. Each of the three screens is a separate
  // document, so without this the arcade forgets you the moment you click
  // another game. Silent by design -- see wallet.resumeAdena.
  if (S.mode === 'live') {
    const back = (await wallet.resumeMnemonic().catch(() => null))
      ?? (await wallet.resumeAdena().catch(() => null));
    if (back) { S.me = back.address; S.master = back; }
  }
  await refresh();
  setInterval(refresh, S.mode === 'rehearsal' ? 2000 : CONFIG.pollMs);
}

/* --- the write paths -------------------------------------------------------
   WIRED 2026-10-07, REWRITTEN 2026-10-08 for the redesigned duel. The names
   are the VIEW'S -- putUp, take, settle -- copied from duel-view.js rather
   than guessed, same as before; what changed is everything underneath them.

   THERE IS NO SECRET ANY MORE, AND THAT IS WHY THESE ARE SHORT. Post takes
   no commitment, Accept takes only an offer ID, and Resolve is permissionless
   and settles in one call. Nothing here has to be saved to disk before a
   transaction is sent, because there is no longer anything whose loss would
   strand money -- the entire reason chain/vault.js existed. */

// THE GATE ITSELF. S.compat is null only in rehearsal, where putUp/take's own
// "The rehearsal does not take stakes" throws first anyway -- there is no
// real contract there to be the wrong version of. In live mode S.compat was
// created and awaited once in start(), so this call resolves from its cache
// and never re-queries the chain; it still has to be awaited here because a
// caller could in principle run before start()'s own await landed.
async function assertCompatible() {
  if (!S.compat) return;
  const r = await S.compat.check();
  if (!r.ok) throw new Error(r.message);
}

function rungOf(stake) {
  const want = BigInt(Math.round(Number(stake) * 1e6));
  const i = S.stakes.findIndex((u) => BigInt(u) === want);
  if (i < 0) throw new Error('That stake is not one of the rungs this realm offers.');
  return i;
}

// Pull `amount` ugnot of wrapped GNOT within reach of the realm, wrapping first
// if the player holds the native kind instead. Same three-step shape as the two
// draw screens; see weekly-data.js for why wrapping is part of it.
// Work out what a stake needs, WITHOUT signing anything. Returns {wrap, approve}
// for msgsFor to fold into one transaction.
async function allowanceNeeds(amount) {
  const spender = realmAddress(REALM);
  const have = BigInt(await live.wugnotAllowance(S.me, spender));
  if (have >= amount) return { wrap: 0n, approve: null, spender };
  const held = BigInt(await live.wugnotBalance(S.me));
  let wrap = 0n;
  if (held < amount) {
    wrap = amount - held;
    const native = BigInt(await live.ugnotBalance(S.me));
    const feeFloor = 2_000_000n;
    if (native < wrap + feeFloor) {
      throw new Error(
        'You need ' + gnot(wrap) + ' more GNOT wrapped and hold ' + gnot(native) + ' unwrapped. '
        + 'Top up from the faucet at https://faucet.gno.land first.',
      );
    }
  }
  return { wrap, approve: amount, spender };
}

// ONE SIGNATURE FOR THE WHOLE MOVE. Adena has always taken an array of
// messages; the arcade just never used it, so a first stake cost three separate
// approval dialogs. Batching is fewer prompts AND safer: the messages are
// atomic, so a failure cannot leave money wrapped and approved for a duel that
// was never posted. STILL RIGHT UNDER THE REDESIGN -- unlike the two-signature
// Accept/RevealNonce split the sealed coin forced (which could NOT be batched;
// see the deleted chain/coin.js for why), Post and Accept are each exactly one
// call again, so wrap+approve+call collapses to one signature same as before.
function msgsFor(pkgPath, func, args, { wrap = 0n, approve = null, spender = null } = {}) {
  const m = S.master;
  if (!m) throw new Error('Connect a wallet first.');
  const json = m.kind === 'adena';
  const out = [];
  if (wrap > 0n) {
    out.push(json
      ? wallet.callJsonSend(S.me, CONFIG.wugnotRealm, 'Deposit', [], String(wrap) + 'ugnot')
      : wallet.callProtoSend(S.me, CONFIG.wugnotRealm, 'Deposit', [], String(wrap) + 'ugnot'));
  }
  if (approve != null) {
    out.push(json
      ? wallet.callJson(S.me, CONFIG.wugnotRealm, 'Approve', [spender, String(approve)])
      : wallet.callProto(S.me, CONFIG.wugnotRealm, 'Approve', [spender, String(approve)]));
  }
  out.push(json ? wallet.callJson(S.me, pkgPath, func, args) : wallet.callProto(S.me, pkgPath, func, args));
  return out;
}

function sendBatch(msgs) {
  const m = S.master;
  if (!m) throw new Error('Connect a wallet first.');
  if (m.kind === 'key') return ports.keySend(m, msgs);
  if (m.kind === 'adena') return ports.adenaSend(msgs);
  throw new Error('That wallet cannot sign from this page.');
}

async function putUp(stake, target, message) {
  // THE COMPATIBILITY GATE, FIRST, BEFORE ANYTHING ELSE IN THIS FUNCTION --
  // before the wallet check. If the configured realm does not speak this
  // ABI, nothing past this line may run: no signature prompt, no wallet
  // call, no money at risk.
  await assertCompatible();
  if (S.mode === 'rehearsal') throw new Error('The rehearsal does not take stakes.');
  if (!S.master || !S.me) throw new Error('Connect a wallet first.');
  const rung = rungOf(stake);
  const amount = BigInt(S.stakes[rung]);
  const needs = await allowanceNeeds(amount);

  // Post(rung, opponent, label) -- NO COMMITMENT. There is nothing to
  // generate and nothing to save before this call: the whole reason putUp
  // used to write to chain/vault.js first no longer applies.
  await sendBatch(msgsFor(REALM, 'Post', [String(rung), target || '', message || ''], needs));
  await refresh();
  return { stake: Number(stake) };
}

async function take(id) {
  // Same gate as putUp, same reason, first for the same reason: nothing past
  // this line may run against an incompatible or unconfirmed realm.
  await assertCompatible();
  if (S.mode === 'rehearsal') throw new Error('The rehearsal does not take stakes.');
  if (!S.master || !S.me) throw new Error('Connect a wallet first.');
  const offer = (S.board?.offers ?? []).find((o) => Number(o.id) === Number(id));
  if (!offer) throw new Error('That offer is no longer on the table.');
  const needs = await allowanceNeeds(BigInt(offer.stake));

  // Accept(offerID) -- JUST THE ID. No nonce to draw, nothing to persist
  // first: the taker-side commitment the sealed coin added is gone along
  // with the poster's secret it was closing a loophole against.
  await sendBatch(msgsFor(REALM, 'Accept', [String(id)], needs));
  await refresh();
  return { id: Number(id) };
}

// Resolve -- the only settle call left, and the only PERMISSIONLESS one the
// old trio had (Reveal was permissionless too; Forfeit and Collect were not).
// Anyone may call it for any duel still in flight; the realm decides the
// winner from the beacon's randomness at the round Accept pinned and pays
// out AND deletes the record in the same call. No secret to look up, no
// deadline to compare against, no branch on who is calling.
async function settle(id) {
  if (S.mode === 'rehearsal') throw new Error('The rehearsal does not settle duels.');
  if (!S.master || !S.me) throw new Error('Connect a wallet first.');
  const d = (S.board?.duels ?? []).find((x) => Number(x.id) === Number(id));
  if (!d) throw new Error('That duel is no longer in flight.');
  await send(REALM, 'Resolve', [String(id)]);
  await refresh();
  return { id: Number(id), did: 'resolve' };
}

function send(pkgPath, func, args) {
  const m = S.master;
  if (!m) throw new Error('Connect a wallet first.');
  if (m.kind === 'key') return ports.keySend(m, [wallet.callProto(S.me, pkgPath, func, args)]);
  if (m.kind === 'adena') return ports.adenaSend([wallet.callJson(S.me, pkgPath, func, args)]);
  throw new Error('That wallet cannot sign from this page.');
}

async function connectWallet() {
  if (S.mode === 'rehearsal') {
    S.me = S.client?.me ?? null;
    await refresh();
    return { address: S.me, kind: 'rehearsal', message: 'Playing as a guest in the rehearsal.' };
  }
  wallet.clearDisconnected(); // an explicit connect overrides an earlier disconnect
  const resumed = await wallet.resumeMnemonic().catch(() => null);
  const w = resumed ?? (wallet.hasAdena()
    ? await wallet.connectAdena()
    : (() => { throw new Error('No wallet found. Install Adena and point it at ' + CONFIG.chainId + '.'); })());
  S.me = w.address;
  S.master = w;
  await refresh();
  return {
    address: w.address,
    kind: w.kind,
    message: 'Connected ' + w.address.slice(0, 8) + '…' + w.address.slice(-4) + '.',
  };
}

// Disconnect. Forgets the wallet HERE and nothing else: a page cannot un-approve
// itself in Adena, and pretending otherwise would be a lie about what happened.
// The flag is what makes it stick across the other two screens.
async function disconnect() {
  wallet.forgetMnemonic();
  wallet.markDisconnected();
  S.me = null;
  S.master = null;
  await refresh();
  return { message: 'Disconnected here. Adena still has this site approved — remove it in the extension to undo that.' };
}

// duel-view.js is the only browser consumer, and it talks to this object,
// never to the exports below. Guarded because importing this module from
// Node (test/duelcompat.test.mjs runs under plain `node --test`, no DOM) hit
// this line unconditionally otherwise -- `window` is simply not defined
// there, and the whole suite failed to load before a single test ran.
if (typeof window !== 'undefined') {
  window.SplashDuel = {
    config: { mode: S.mode, network: CONFIG.chainId, realm: REALM },
    start,
    onChange(fn) { listeners.push(fn); if (state && !state.offline) fn({ type: 'state', state }); },
    putUp,
    take,
    settle,
    connectWallet,
    disconnect,
    say,
  };
}

// say posts one message to the room.
//
// NO COMPATIBILITY GATE AND NO allowanceNeeds, UNLIKE putUp AND take, and the
// difference is deliberate rather than an omission: this call moves no money.
// chat.Post takes no coin, holds no escrow and has no allowance -- the realm
// refuses attached coin outright. So the gate that exists to stop a signature
// going out against a realm whose ABI we cannot verify has nothing to protect
// here; the worst case is a failed transaction that costs gas and changes
// nothing.
//
// THE LENGTH CHECK IS LOCAL AND IN BYTES, matching the contract exactly. The
// realm measures len(text) -- BYTES, not runes, because bytes are what storage
// costs -- so a 200-character message of emoji is over a 280-BYTE cap while
// looking well under it. Checking runes here would let the browser wave
// through something the chain then refuses, charging the player gas to be told
// no. TextEncoder is how JavaScript counts the same thing Go's len() does.
async function say(text) {
  if (S.mode === 'rehearsal') throw new Error('The rehearsal has no room.');
  if (!S.master || !S.me) throw new Error('Connect a wallet first.');
  const body = String(text ?? '').trim();
  if (!body) throw new Error('Say something first.');
  const bytes = new TextEncoder().encode(body).length;
  if (bytes > S.chatMaxLen) {
    throw new Error(`That is ${bytes} bytes and the limit is ${S.chatMaxLen}.`
      + ' (Emoji and accents cost more than one each.)');
  }
  await sendBatch(msgsFor(CONFIG.chatRealm, 'Post', [body], {}));
  await refreshChat();
  publish();
}

// --- exported for test/duelcompat.test.mjs only ----------------------------
// Nothing in src/ or duel.html imports these; duel-view.js talks to
// window.SplashDuel exclusively (above). S is the module's one piece of
// state, exported by reference so a test can set up S.mode/S.master/S.compat
// directly -- the same object putUp/take/assertCompatible already close
// over, not a copy. ports is the signing seam just above REALM.
export { S, putUp, take, assertCompatible, REALM };
